"use client";
import React, { useState } from "react";
import Image from "next/image";

import shape_1 from "@/assets/img/services/shape/services-shape-1.png";
import shape_2 from "@/assets/img/services/shape/services-shape-2.png";
import square_shape_bottom from "@/assets/img/bg/square-shape-bottom.png";
import square_shape_top from "@/assets/img/bg/square-shape-top.png";
import Link from "next/link";

const service_content = {
  subtitle: <>What's in a name?</>,
  title: <>MUSE Content</>,
  sm_des: (
    <>
      {
        "Ancient Latin and Greek poets appealed to a muse to help them write. Invoking the muse meant to call upon inspiration and higher thinking to elevate their work."
      }
    </>
  ),
  accordion_data: [
    {
      id: 1,
      tab_id: "One",
      question: "Ghostwriting",
      answer:
        "As a bestselling ghostwriter, I pull from my background in journalism to tell your story in your voice. My process includes:",
      some_features: [
        "Interviewing and crafting your narrative",
        "Writing, co-writing, and editing",
        "Liaising with publishers and project managing",
      ],
      learn_more_text: "Learn more about ghostwriting here",
      learn_more_link: "/ghostwriting"
    },
    {
      id: 2,
      tab_id: "Two",
      question: "Developmental Editing",
      answer:
        "With developmental editing experience in books on business, lifestyle, and memoir, I can help you take your book from an early draft to a final, polished product. Some of my skills include:",
      some_features: [
        "Structuring your book and weaving in your story",
        "Resolving plot and pacing issues",
        "Proofreading for consistency and continuity",
      ],
      learn_more_text: "Learn more about developmental editing here",
      learn_more_link: "/developmental-editing"
    },
    {
      id: 3,
      tab_id: "Three",
      question: "Idea Development",
      answer:
        "Crafted from the early phase of my ghostwriting process, I’ll help you explore your ideas and expertly facilitate them into something coherent. This service can help you:",
      some_features: [
        "Solidify your book idea into an outline",
        "Untangle your message for speaking engagements",
        "Prepare for board or funding meetings",
      ],
      learn_more_text: "Learn more about idea development here",
      learn_more_link: "/idea-development"
    },
    {
      id: 4,
      tab_id: "Four",
      question: "Content Strategy",
      answer:
        "Pulling from my 15+ years of leading in-house teams in the health and science space, I specialise in reaching your audience with credibility. Along with my team of writers, scientists, and editors, I can:",
      some_features: [
        "Implement an SEO strategy",
        "Optimise email, website, and product copy",
        "Create features and lead magnets",
      ],
    },
    {
      id: 5,
      tab_id: "Five",
      question: "Tone of Voice",
      answer:
        "It’s not just what you say, it’s how you say it. With experience creating brand tone of voice and training entire companies in its use, I can help you with:",
      some_features: [
        "Defining your tone of voice",
        "Creating guidelines",
        "Training teams",
      ],
    },
    {
      id: 6,
      tab_id: "Six",
      question: "Style Guides",
      answer:
        "A company’s style guide forms the bedrock of consistency. Beyond whether you follow AP style or use the Oxford comma, adhering to a cohesive company style signals credibility to your audience. I can help with:",
      some_features: [
        "Defining your company style",
        "Creating a catch-all style guide",
        "Training editors and writers in your style",
      ],
    }
  ],
};

const { subtitle, title, sm_des, accordion_data } = service_content;

const MuseServiceArea = () => {
  const [active, setActive] = useState(0);

  const handleItemClick = (index: number) => {
    setActive(index);
  };

  return (
    <section id="services">
      <div className="overlay-bg tp-sv pb-50 fix p-relative">
        <div className="container container-large">
          <div className="tp-services-inner p-relative z-index-1">
            <div className="row gx-0">
              <div className="col-xl-6 col-lg-7">
                <div
                  className="tp-services-wrapper pt-50 pr-70"
                >
                  <div className="tp-section-title-wrapper">
                    <div className="tp-section-title-inner p-relative">
                      <span className="tp-section-subtitle">{subtitle}</span>
                      <h3 className="tp-section-title tp_title_anim">
                        {title}
                      </h3>
                    </div>
                    <p>{sm_des}</p>
                  </div>
                </div>
              </div>

              <div className="col-xl-6 col-lg-5">
                <div
                  className="tp-services-accordion tp-accordion pl-70 p-relative"
                  style={{ marginTop: "40px" }}
                >
                  <span className="tp-services-accordion-border"></span>
                  <div className="accordion" id="services-accordion">
                    {accordion_data.map((item, i) => (
                      <div
                        key={i}
                        onClick={() => handleItemClick(i)}
                        className={`accordion-item tp-services-accordion-item ${
                          active === i ? "active" : ""
                        }`}
                      >
                        <h2
                          className="accordion-header"
                          id={`heading${item.tab_id}`}
                        >
                          <button
                            className={`accordion-button ${
                              i === 0 ? "" : "collapsed"
                            }`}
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#collapse${item.tab_id}`}
                            aria-expanded={`${i === 0 ? "true" : "false"}`}
                            aria-controls={`collapse${item.tab_id}`}
                            tabIndex={0}
                          >
                            <span>0{item.id}</span>
                            {item.question}
                            <span className="accordion-btn-wrap">
                              <span className="accordion-btn"></span>
                            </span>
                          </button>
                        </h2>
                        <div
                          id={`collapse${item.tab_id}`}
                          className={`accordion-collapse collapse ${
                            i === 0 ? "show" : ""
                          }`}
                          aria-labelledby={`heading${item.tab_id}`}
                          data-bs-parent="#services-accordion"
                        >
                          <div className="accordion-body">
                            <p>{item.answer}</p>
                            <ul>
                              {item.some_features.map((feature, index) => (
                                <li key={index}>{feature}</li>
                              ))}
                            </ul>
                            {item.learn_more_text && 
                              <p><Link href={item.learn_more_link}>{item.learn_more_text} &gt;</Link></p>
                            }
                          </div>
                        </div>
                        <span className="accordion-item-border"></span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="ab-hero__shape-2">
          <Image src={square_shape_bottom} alt="image-here" />
        </div>
      </div>
    </section>
  );
};

export default MuseServiceArea;
